import { useCallback, useState } from 'react';
import { api } from '../../app/http-common';

/**
 * The two-step confirmation for adding, moving or copying a story into a bucket
 * that refuses one of its languages.
 *
 * ONE HOOK FOR EVERY MANUAL SCREEN, deliberately. The same question is asked
 * from the Stories screen, the Articles screen, the Copy to bucket modal and
 * Bucket Stories, and each of those is a different component with its own
 * toast and its own idea of what a destination is. Five copies of this dialog
 * would be five chances for one of them to skip the second step, which is the
 * drift CLAUDE.md gotcha 25 is about and the exact shape of the pre-send purge
 * defect: one correct-looking feature that only ran from one of its two call
 * sites.
 *
 * WHY TWO STEPS. Orr, 2026-11-01. The first dialog in a drag-and-drop flow
 * lands mid-gesture and is dismissed reflexively, so the step that names the
 * client is a second, separate decision.
 *
 * THE VERDICT COMES FROM THE SERVER. This hook never decides whether a story is
 * excluded; it asks `GET /publisher-buckets/:id/exclusion-check/:storyId` and
 * renders what comes back. A copy of the rule here would be the copy an
 * administrator reads, and it would drift from the one that actually refuses
 * the write.
 *
 * Usage:
 *
 *   const confirm = useExclusionConfirm();
 *   const ok = await confirm.request({ bucketId, storyId, bucketName });
 *   if (!ok) return;                       // cancelled, or blocked
 *   await api.addStoryToBucket(bucketId, storyId, scope, {}, {
 *     overrideExclusion: ok.override, screen: 'stories',
 *   });
 *
 * and render <ExclusionWarningModal {...confirm.modalProps} /> once.
 */
export default function useExclusionConfirm() {
  const [state, setState] = useState(null);

  const close = useCallback(() => {
    setState((prev) => {
      prev?.resolve?.(null);
      return null;
    });
  }, []);

  /**
   * Returns a promise for the decision:
   *   { override: false } proceed, nothing was excluded
   *   { override: true }  proceed, the administrator overrode the exclusion
   *   null                do not proceed (cancelled, or the bucket is enforcing)
   */
  const request = useCallback(async ({ bucketId, storyId, bucketName = null }) => {
    let verdict;
    try {
      verdict = await api.checkBucketExclusion(bucketId, storyId);
    } catch (err) {
      // FAIL OPEN, and this is a deliberate choice rather than an oversight.
      // The server gate is the thing that actually refuses the write, so a
      // failed pre-check cannot admit anything the gate would refuse: the add
      // simply fails with a 422 instead of a dialog. Failing closed here would
      // block adding a story to any bucket whenever this one endpoint is
      // unavailable, which is a much larger outage than a worse error message.
      console.warn('[exclusion] pre-check failed, deferring to the server gate:', err?.message || err);
      return { override: false };
    }

    const violations = verdict?.violations || [];
    if (!violations.length) return { override: false };

    return new Promise((resolve) => {
      setState({
        verdict: { ...verdict, bucketName: verdict.bucketName || bucketName },
        step: 1,
        resolve,
      });
    });
  }, []);

  const onFirstContinue = useCallback(() => {
    setState((prev) => (prev ? { ...prev, step: 2 } : prev));
  }, []);

  const onSecondConfirm = useCallback(() => {
    setState((prev) => {
      prev?.resolve?.({ override: true });
      return null;
    });
  }, []);

  return {
    request,
    modalProps: {
      verdict: state?.verdict || null,
      step: state?.step || 0,
      onCancel: close,
      onFirstContinue,
      onSecondConfirm,
    },
  };
}
